import type { ProblemTemplate } from '../../types.ts';
import { continuation, exactDiv, integerProblem, span, UNKNOWNS } from './helpers-g05-08.ts';

/**
 * Grade 5 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 *
 * CAPS Grade 5: number sentences with letters, whole numbers only (no
 * negatives anywhere), inverse operations, input → output flow diagrams and
 * the rule of a constant-difference pattern. Multiplication is always written
 * with × (Intermediate Phase notation): "4 × m", never the Senior Phase "4m".
 */

const varSub: ProblemTemplate = {
  id: 'g5.var-sub',
  grade: 5,
  title: 'Solve x − b = c (answer ≤ 200)',
  parTimeMs: 9000,
  allowNegative: false,
  generate(rng, difficulty) {
    // Single digits at difficulty 0; up to 99 − … = 100 (answer ≤ 199) at the top.
    const b = rng.int(span(difficulty, 2, 11), span(difficulty, 9, 99));
    const c = rng.intExcept(span(difficulty, 2, 10), span(difficulty, 12, 100), [b]);
    const answer = b + c;
    const v = rng.pick(UNKNOWNS);
    const carries = (b % 10) + (c % 10) >= 10;
    return integerProblem({
      prompt: `${v} - ${b} = ${c}`,
      instruction: { key: 'solve', params: { v } },
      answer,
      allowNegative: false,
      distractors: [
        Math.abs(c - b), // inverse-operation error: subtracted b again (smaller from larger) instead of adding it back
        carries ? answer - 10 : null, // regrouping slip: added the columns but forgot the carried ten
        answer - 1, // counting on from c by b, but counted c itself as the first step
      ],
    });
  },
};

const coefMult: ProblemTemplate = {
  id: 'g5.coef-mult',
  grade: 5,
  title: 'Solve a × x = c (past the times tables)',
  parTimeMs: 9000,
  allowNegative: false,
  generate(rng, difficulty) {
    const a = rng.int(2, span(difficulty, 5, 12));
    const x = rng.int(2, span(difficulty, 6, 15));
    const c = a * x;
    const v = rng.pick(UNKNOWNS);
    // The letter comes first sometimes in the upper half, so the order of the factors is not a cue.
    const product = difficulty >= 0.4 && rng.chance(0.4) ? `${v} \\times ${a}` : `${a} \\times ${v}`;
    return integerProblem({
      prompt: `${product} = ${c}`,
      instruction: { key: 'solve', params: { v } },
      answer: x,
      allowNegative: false,
      distractors: [
        c - a, // wrong inverse operation: subtracted a instead of dividing by it
        x + 1, // times-table slip: landed on the neighbouring multiple
        x - 1,
      ],
    });
  },
};

const flowRule: ProblemTemplate = {
  id: 'g5.flow-rule',
  grade: 5,
  title: 'Flow diagram: y = a × x ± b, given x',
  parTimeMs: 11000,
  allowNegative: false,
  generate(rng, difficulty) {
    const a = rng.int(2, span(difficulty, 5, 12));
    const x = rng.int(2, span(difficulty, 5, 12));
    const bMax = span(difficulty, 9, 30);
    // "× then −" rules only in the upper half; b < ax keeps the output a whole number ≥ 1.
    const sign = difficulty >= 0.5 && rng.chance(0.4) ? -1 : 1;
    const b = sign < 0 ? rng.int(1, Math.min(bMax, a * x - 1)) : rng.int(1, bMax);
    const sb = sign * b;
    return integerProblem({
      prompt: `y = ${a} \\times x${continuation({ coef: sb })} \\sep x = ${x}`,
      instruction: { key: 'findY', params: { v: 'y' } },
      answer: a * x + sb,
      allowNegative: false,
      distractors: [
        a + x + sb, // used + for the × step
        a * x, // stopped after the first step of the flow diagram
        a * (x + sb), // order of operations: added b to x before multiplying
        a * x - sb, // sign slip on the constant
      ],
    });
  },
};

const twoOpMissing: ProblemTemplate = {
  id: 'g5.two-op-missing',
  grade: 5,
  title: 'Two operations: □ × a ± b = c',
  parTimeMs: 13000,
  allowNegative: false,
  generate(rng, difficulty) {
    const a = rng.int(2, span(difficulty, 5, 12));
    const answer = rng.int(2, span(difficulty, 6, 12));
    const bMax = span(difficulty, 9, 50);
    const minus = difficulty >= 0.6 && rng.chance(0.35);
    // "−" form: b < a·□ keeps c ≥ 1, and b ≠ (a − 1)·□ keeps c ≠ □ ("□ × 3 − 4 = 2" could be copied).
    const b = minus ? rng.intExcept(1, Math.min(bMax, a * answer - 1), [(a - 1) * answer]) : rng.int(1, bMax);
    const c = a * answer + (minus ? -b : b);
    const boxFirst = difficulty < 0.35 || rng.chance(0.6);
    const product = boxFirst ? `\\box \\times ${a}` : `${a} \\times \\box`;
    const quotient = exactDiv(c, a);
    return integerProblem({
      prompt: `${product} ${minus ? '-' : '+'} ${b} = ${c}`,
      answer,
      allowNegative: false,
      distractors: [
        minus ? c + b : c - b, // undid the ± b but forgot to divide by a
        exactDiv(minus ? c - b : c + b, a), // inverse-operation slip: added b instead of subtracting (or vice versa)
        quotient === null ? null : minus ? quotient + b : quotient - b, // undid the operations in the wrong order
        answer + 1, // times-table slip
      ],
    });
  },
};

const patternTerm: ProblemTemplate = {
  id: 'g5.pattern-term',
  grade: 5,
  title: 'Term n of a constant-difference pattern',
  parTimeMs: 16000,
  allowNegative: false,
  generate(rng, difficulty) {
    const first = rng.int(1, span(difficulty, 5, 20));
    const step = rng.int(2, span(difficulty, 5, 12));
    // Just past the shown terms at difficulty 0 (keep counting on); far enough at the top that a rule is needed.
    const n = rng.int(span(difficulty, 6, 10), span(difficulty, 8, 30));
    const shown = [0, 1, 2, 3].map((i) => first + i * step);
    // "Term 8 is double term 4": treating the pattern as proportional (only when a shown term fits).
    const scaleFrom = [4, 3, 2].find((m) => n % m === 0);
    const proportional = scaleFrom === undefined ? null : shown[scaleFrom - 1]! * (n / scaleFrom);
    return integerProblem({
      prompt: `${shown.join(', ')}, \\dots`,
      instruction: { key: 'term', params: { n } },
      answer: first + (n - 1) * step,
      allowNegative: false,
      distractors: [
        first + n * step, // off-by-one: took n steps from the first term instead of n − 1
        n * step, // position × difference, ignoring where the pattern starts
        proportional,
        first + (n - 2) * step, // off-by-one the other way: stopped a term early
      ],
    });
  },
};

export const GRADE_05: ProblemTemplate[] = [varSub, coefMult, flowRule, twoOpMissing, patternTerm];
